import { useTranslation } from 'react-i18next';
import { useBusinessInfo, BusinessHours } from '@/hooks/useBusinessInfo';

const formatTime = (time: string) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hours = h % 12 || 12;
  return `${hours}:${m.toString().padStart(2, '0')} ${ampm}`;
};

const days: (keyof BusinessHours)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const todayIndex = new Date().getDay(); // 0=Sun
const todayArrayIndex = todayIndex === 0 ? 6 : todayIndex - 1;

export default function ContactInfo() {
  const { t } = useTranslation();
  const { info, waLink, isOpenNow } = useBusinessInfo();

  const dynamicSocialLinks = [
    {
      icon: 'ri-instagram-line',
      label: 'Instagram',
      handle: 'Instagram',
      href: info.instagram,
      color: 'hover:text-pink-400',
      active: !!info.instagram
    },
    {
      icon: 'ri-facebook-line',
      label: 'Facebook',
      handle: 'Facebook',
      href: info.facebook,
      color: 'hover:text-blue-400',
      active: !!info.facebook
    },
    {
      icon: 'ri-tiktok-line',
      label: 'TikTok',
      handle: 'TikTok',
      href: info.tiktok,
      color: 'hover:text-white',
      active: !!info.tiktok
    },
  ].filter(s => s.active);

  return (
    <div className="flex flex-col gap-6">
      {/* Address */}
      <div className="bg-[#111111] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 flex items-center justify-center bg-[#F57F25]/10 border border-[#F57F25]/20 rounded-lg shrink-0">
            <i className="ri-map-pin-line text-[#F57F25] text-lg"></i>
          </div>
          <h3 className="text-white font-bold text-sm uppercase tracking-widest">{t('location_address_title')}</h3>
        </div>
        <p className="text-white/80 text-base font-medium leading-relaxed mb-3">
          {info.address}
        </p>
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(info.address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[#F57F25] text-xs font-semibold hover:underline cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-external-link-line text-xs"></i>
          </div>
          {t('btn_open_maps')}
        </a>
      </div>

      {/* Phone */}
      <div className="bg-[#111111] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 flex items-center justify-center bg-[#F57F25]/10 border border-[#F57F25]/20 rounded-lg shrink-0">
            <i className="ri-phone-line text-[#F57F25] text-lg"></i>
          </div>
          <h3 className="text-white font-bold text-sm uppercase tracking-widest">{t('location_phone_title')}</h3>
        </div>
        <a
          href={info.phone ? `tel:${info.phone.replace(/\\D/g, '')}` : '#'}
          className="text-white/80 text-xl font-bold hover:text-[#F57F25] transition-colors cursor-pointer"
        >
          {info.phone}
        </a>
        <p className="text-white/35 text-xs mt-1">{t('location_phone_tap')}</p>
      </div>

      {/* WhatsApp */}
      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-4 bg-[#25D366]/10 border border-[#25D366]/25 hover:border-[#25D366]/50 rounded-2xl p-6 transition-all duration-200 cursor-pointer group"
      >
        <div className="w-12 h-12 flex items-center justify-center bg-[#25D366] rounded-xl shrink-0 group-hover:scale-105 transition-transform">
          <i className="ri-whatsapp-line text-white text-2xl"></i>
        </div>
        <div>
          <p className="text-white font-bold text-base">{t('location_wa_title')}</p>
          <p className="text-white/45 text-xs mt-0.5">{t('location_wa_sub')}</p>
        </div>
        <div className="ml-auto w-6 h-6 flex items-center justify-center text-white/30 group-hover:text-[#25D366] transition-colors">
          <i className="ri-arrow-right-line text-lg"></i>
        </div>
      </a>

      {/* Business Hours */}
      <div className="bg-[#111111] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#F57F25]/10 border border-[#F57F25]/20 rounded-lg shrink-0">
              <i className="ri-time-line text-[#F57F25] text-lg"></i>
            </div>
            <h3 className="text-white font-bold text-sm uppercase tracking-widest">{t('location_hours_title')}</h3>
          </div>
          {/* Status Badge */}
          <div className={`px-2.5 py-1 rounded-md text-xs font-black uppercase tracking-widest ${isOpenNow ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
            {isOpenNow ? 'Open Now' : 'Closed'}
          </div>
        </div>
        <ul className="space-y-2.5">
          {info.hours && days.map((dayKey, idx) => {
            const h = info.hours![dayKey];
            const isToday = idx === todayArrayIndex;
            const isClosed = !h.isOpen;
            return (
              <li
                key={dayKey}
                className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 transition-colors ${
                  isToday ? 'bg-[#F57F25]/10 border border-[#F57F25]/20' : ''
                }`}
              >
                <span className={`font-medium capitalize ${isToday ? 'text-[#F57F25]' : 'text-white/60'}`}>
                  {t(`day_${dayKey}`)}
                  {isToday && (
                    <span className="ml-2 text-[10px] bg-[#F57F25] text-black font-bold px-1.5 py-0.5 rounded-full uppercase">
                      {t('location_today')}
                    </span>
                  )}
                </span>
                <span className={`font-semibold ${isClosed ? 'text-white/25' : isToday ? 'text-white' : 'text-white/70'}`}>
                  {isClosed ? t('location_closed') : `${formatTime(h.openTime)} – ${formatTime(h.closeTime)}`}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Social Links */}
      <div className="bg-[#111111] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 flex items-center justify-center bg-[#F57F25]/10 border border-[#F57F25]/20 rounded-lg shrink-0">
            <i className="ri-share-line text-[#F57F25] text-lg"></i>
          </div>
          <h3 className="text-white font-bold text-sm uppercase tracking-widest">{t('location_social_title')}</h3>
        </div>
        <div className="flex flex-col gap-3">
          {dynamicSocialLinks.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-3 text-white/55 ${s.color} transition-colors cursor-pointer group`}
            >
              <div className="w-9 h-9 flex items-center justify-center bg-white/5 group-hover:bg-white/10 rounded-lg transition-colors">
                <i className={`${s.icon} text-lg`}></i>
              </div>
              <div>
                <p className="text-sm font-semibold">{s.label}</p>
                <p className="text-xs text-white/30">{s.handle}</p>
              </div>
              <div className="ml-auto w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-external-link-line text-xs"></i>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
